import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useChargeLinesPreviewMutation } from '@features/billing/store/v2-api';
import type {
  ChargeLinePreviewRequest,
  ChargeLinePreviewResponse,
} from '@store/generated/billing-v2';

interface UsePreviewByEncounter {
  data?: ChargeLinePreviewResponse;
  isLoading: boolean;
  error: unknown;
  retry: () => void;
}

type PreviewTarget =
  | { encounterId: string; patientId?: never; chargeLineIds?: never }
  | { patientId: string; encounterId?: never; chargeLineIds?: never }
  | { chargeLineIds: string[]; encounterId?: never; patientId?: never };

/**
 * Wrapper around POST /v2/provider/charge-lines/preview.
 *
 * The mutation underneath is `usePostV2ProviderChargeLinesPreviewMutation`.
 * We treat it as a query — re-fires whenever the preview target or the
 * `refreshKey` changes, and exposes the response as `data` for query-shaped
 * consumers (drawer Receipt section, SettleSheet defaults).
 *
 * The backend accepts exactly one of encounter_id, patient_id, or
 * charge_line_ids. Patient scope uses patient_id so the backend owns the
 * invoiceable line-set suggestion.
 *
 * Pass a `refreshKey` derived from a value that should trigger a re-fetch
 * when the underlying data changes — usually the financial summary's
 * entry-count or last-entry-id for the encounter. Mutations like
 * captureCharges / recordPayment invalidate the financial summary tag
 * automatically; this prop bridges that refetch into the preview, since
 * mutations don't participate in RTK Query's tag-based cache.
 */
export function useChargeLinePreviewTarget(
  target: PreviewTarget | null | undefined,
  refreshKey?: string | number,
): UsePreviewByEncounter {
  const [trigger, { data, isLoading, error, originalArgs }] = useChargeLinesPreviewMutation();
  const targetKey = previewTargetKey(target);
  const request = useMemo(() => previewRequestFromKey(targetKey), [targetKey]);

  // Stash the mutation trigger in a ref so it can't appear in the effect
  // deps. RTK Query's mutation tuple gets a fresh function reference every
  // render — listing it directly in deps creates a render → call → state
  // change → re-render loop.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  const retry = useCallback(() => {
    if (!request) return;
    void triggerRef.current({
      chargeLinePreviewRequest: request,
    });
  }, [request]);

  useEffect(() => {
    retry();
  }, [retry, refreshKey]);

  // RTK keeps the previous mutation result until the effect starts the new
  // request. Only a result for this target can seed a money selection.
  const isCurrentTarget = !!request &&
    JSON.stringify(originalArgs?.chargeLinePreviewRequest) === JSON.stringify(request);
  return {
    data: isCurrentTarget ? data : undefined,
    isLoading: !!request && (!isCurrentTarget || isLoading),
    error: isCurrentTarget ? error : undefined,
    retry,
  };
}

export function usePreviewByEncounter(
  encounterId: string | null | undefined,
  refreshKey?: string | number,
): UsePreviewByEncounter {
  return useChargeLinePreviewTarget(
    encounterId ? { encounterId } : null,
    refreshKey,
  );
}

function previewTargetKey(target: PreviewTarget | null | undefined): string {
  if (!target) return '';
  if ('encounterId' in target) return `encounter:${target.encounterId}`;
  if ('patientId' in target) return `patient:${target.patientId}`;
  return `lines:${[...target.chargeLineIds].sort().join(',')}`;
}

function previewRequestFromKey(targetKey: string): ChargeLinePreviewRequest | null {
  if (!targetKey) return null;
  const separatorIndex = targetKey.indexOf(':');
  if (separatorIndex === -1) return null;
  const kind = targetKey.slice(0, separatorIndex);
  const value = targetKey.slice(separatorIndex + 1);
  if (!value) return null;
  if (kind === 'encounter') return { encounter_id: value };
  if (kind === 'patient') return { patient_id: value };
  if (kind === 'lines') return { charge_line_ids: value.split(',').filter(Boolean) };
  return null;
}
